// Подсветка «новых» блоков библиотеки: водяная метка «просмотрено» в
// localStorage + сортировка/проверка свежести. Чистые функции без React —
// компонент только читает/пишет через них.

const SEEN_KEY = 'plan-flights.library.seen.v1';
const READ_KEY = 'plan-flights.library.read.v1';
/** Блок «новый» не дольше недели: забытая метка «просмотрено» иначе светила бы вечно. */
const NEW_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

type SeenMap = Record<string, number>;
/** scope -> { id блока: когда прочитан }. Момент нужен, чтобы чистить старьё. */
type ReadMap = Record<string, Record<string, number>>;

/** Приватный режим и битый JSON — не повод ронять панель библиотеки. */
function readMap(): SeenMap {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const out: SeenMap = {};
    for (const [scope, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'number' && Number.isFinite(value)) out[scope] = value;
    }
    return out;
  } catch {
    return {};
  }
}

function writeMap(map: SeenMap): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(map));
  } catch {
    // Хранилище недоступно (приватный режим, квота) — просто не запоминаем.
  }
}

/** Момент последнего просмотра библиотеки для данной поездки/scope; 0 — никогда. */
export function loadSeenAt(scope: string): number {
  return readMap()[scope] ?? 0;
}

/** Отметить библиотеку просмотренной на момент `at` для данного scope. */
export function markSeen(scope: string, at: number): void {
  const map = readMap();
  map[scope] = at;
  writeMap(map);
}

/** Приватный режим и битый JSON — не повод ронять панель библиотеки. */
function readReadMap(): ReadMap {
  try {
    const raw = localStorage.getItem(READ_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const out: ReadMap = {};
    for (const [scope, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value !== 'object' || value === null || Array.isArray(value)) continue;
      const ids: Record<string, number> = {};
      for (const [id, at] of Object.entries(value as Record<string, unknown>)) {
        if (typeof at === 'number' && Number.isFinite(at)) ids[id] = at;
      }
      if (Object.keys(ids).length > 0) out[scope] = ids;
    }
    return out;
  } catch {
    return {};
  }
}

function writeReadMap(map: ReadMap): void {
  try {
    localStorage.setItem(READ_KEY, JSON.stringify(map));
  } catch {
    // Хранилище недоступно (приватный режим, квота) — просто не запоминаем.
  }
}

/**
 * Метка «прочитано» переживает окно новизны ровно настолько, насколько нужна:
 * старше недели блок и так не новый, а удалённые блоки иначе копились бы в
 * хранилище навсегда.
 */
function pruneReads(map: ReadMap, now: number): ReadMap {
  const out: ReadMap = {};
  for (const [scope, ids] of Object.entries(map)) {
    const kept: Record<string, number> = {};
    for (const [id, at] of Object.entries(ids)) {
      if (now - at < NEW_WINDOW_MS) kept[id] = at;
    }
    if (Object.keys(kept).length > 0) out[scope] = kept;
  }
  return out;
}

/** Прочитанные блоки данного scope на момент `now`; просроченные метки отброшены. */
export function loadReadIds(scope: string, now: number): Set<string> {
  return new Set(Object.keys(pruneReads(readReadMap(), now)[scope] ?? {}));
}

/** Отметить один блок прочитанным: клик по карточке снимает с неё подсветку. */
export function markSegmentRead(scope: string, id: string, at: number): void {
  const map = pruneReads(readReadMap(), at);
  map[scope] = { ...map[scope], [id]: at };
  writeReadMap(map);
}

/** Забыть отметки этого scope: после «просмотрено всё» они уже ничего не решают. */
export function clearSegmentReads(scope: string): void {
  const map = readReadMap();
  if (!(scope in map)) return;
  delete map[scope];
  writeReadMap(map);
}

/**
 * Блок «новый», если добавлен позже отметки «просмотрено», не старше недели и
 * по нему ещё не кликали (`readIds` — то, что вернул loadReadIds).
 */
export function isNewSegment(
  seg: { id: string; addedAt?: number },
  seenAt: number,
  now: number,
  readIds?: ReadonlySet<string>,
): boolean {
  if (seg.addedAt === undefined) return false;
  if (seg.addedAt <= seenAt) return false;
  if (readIds?.has(seg.id) === true) return false;
  return now - seg.addedAt < NEW_WINDOW_MS;
}

/**
 * Новые сверху; блоки без addedAt (заведены до появления фичи или руками до
 * этого патча) уходят вниз. Сортировка стабильна: у равных addedAt и у пары
 * «оба без метки» относительный порядок не меняется.
 */
export function sortByAddedDesc<T extends { addedAt?: number }>(items: readonly T[]): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const aAt = a.item.addedAt;
      const bAt = b.item.addedAt;
      if (aAt === undefined && bAt === undefined) return a.index - b.index;
      if (aAt === undefined) return 1;
      if (bAt === undefined) return -1;
      if (aAt !== bAt) return bAt - aAt;
      return a.index - b.index;
    })
    .map(({ item }) => item);
}
